import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

// Ten endpoint musi być dostępny po stronie backendu aplikacji.
// Oczekiwana odpowiedź: { pid: "05335", name: "Imię Nazwisko", wk: 3 }
const getCezarPlayerUrl = (pid) =>
  `https://brydzwroclaw.com/v1/cezar/player/${encodeURIComponent(pid)}`;

const WK_OPTIONS = [
  "0.0",
  "0.5",
  "1.0",
  "1.5",
  "2.0",
  "2.5",
  "3",
  "4",
  "5",
  "7",
  "9",
  "11",
  "12",
  "13",
  "15",
  "17",
  "18",
  "19",
  "21",
  "24",
];

const normalizeCezarName = (rawName) => {
  if (typeof rawName !== "string" || !rawName.trim()) {
    return "";
  }

  const normalizedName = rawName
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim();

  const words = normalizedName.split(" ");

  // Imię i nazwisko bez przedrostka pozostaje bez zmian.
  if (words.length <= 2) {
    return normalizedName;
  }

  // Rozpoznawane oznaczenia, np.:
  // A12, AS, 12, MM7, MK5.
  const isCezarMarker = (word) =>
    /^(?:AS|[A-Z]*\d+[A-Z]*|[A-Z]{1,3}\d{1,2})$/i.test(word);

  // Sprawdzamy token przed drugą i trzecią spacją od prawej.
  for (const spacesFromRight of [2, 3]) {
    const markerIndex = words.length - spacesFromRight - 1;

    if (markerIndex < 0) {
      continue;
    }

    if (isCezarMarker(words[markerIndex])) {
      // Usuwamy przedrostek wraz z oznaczeniem.
      // Pozostawiamy całą nazwę znajdującą się po nim.
      return words.slice(markerIndex + 1).join(" ").trim();
    }
  }

  // Nie znaleziono oznaczenia — zachowujemy oryginalną nazwę.
  return normalizedName;
};

const RegistrationForm = ({ addPlayerSubmit }) => {
  const [Name1, setName1] = useState("");
  const [Name2, setName2] = useState("");
  const [WK1, setWK1] = useState("");
  const [WK2, setWK2] = useState("");
  const [PID1, setPID1] = useState("");
  const [PID2, setPID2] = useState("");

  const [manual1, setManual1] = useState(false);
  const [manual2, setManual2] = useState(false);
  const [player1Loading, setPlayer1Loading] = useState(false);
  const [player2Loading, setPlayer2Loading] = useState(false);
  const [player1Error, setPlayer1Error] = useState("");
  const [player2Error, setPlayer2Error] = useState("");

  // Identyfikatory zapytań zapobiegają nadpisaniu nowszego PID-u
  // odpowiedzią ze starszego, wolniej zakończonego zapytania.
  const lookupSequence = useRef({ 1: 0, 2: 0 });

  const navigate = useNavigate();
  const { slug } = useParams();

  const updatePid = (value, playerNumber) => {
    const digits = value.replace(/\D/g, "").slice(0, 5);
    lookupSequence.current[playerNumber] += 1;

    if (playerNumber === 1) {
      setPID1(digits);
      setName1("");
      setWK1("");
      setPlayer1Error("");
      setPlayer1Loading(false);
      setManual1(/^0+$/.test(digits));
    } else {
      setPID2(digits);
      setName2("");
      setWK2("");
      setPlayer2Error("");
      setPlayer2Loading(false);
      setManual2(/^0+$/.test(digits));
    }
  };

  const fetchPlayer = async (pid, playerNumber) => {
    const digits = String(pid ?? "").replace(/\D/g, "").slice(0, 5);
    const requestId = ++lookupSequence.current[playerNumber];
    const isFirstPlayer = playerNumber === 1;
    const setPid = isFirstPlayer ? setPID1 : setPID2;
    const setName = isFirstPlayer ? setName1 : setName2;
    const setWK = isFirstPlayer ? setWK1 : setWK2;
    const setManual = isFirstPlayer ? setManual1 : setManual2;
    const setLoading = isFirstPlayer ? setPlayer1Loading : setPlayer2Loading;
    const setError = isFirstPlayer ? setPlayer1Error : setPlayer2Error;

    if (!digits) {
      setManual(false);
      setName("");
      setWK("");
      setError("");
      setLoading(false);
      return;
    }

    // PID równy zero oznacza zawodnika spoza bazy Cezara.
    // Zachowujemy wpisane ręcznie imię i WK także przy ponownym opuszczeniu pola PID.
    if (/^0+$/.test(digits)) {
      setPid("0");
      setManual(true);
      setError("");
      setLoading(false);
      return;
    }

    const normalizedPid = digits.padStart(5, "0");
    setPid(normalizedPid);
    setManual(false);
    setName("");
    setWK("");
    setError("");
    setLoading(true);

    try {
      const response = await axios.get(getCezarPlayerUrl(normalizedPid));

      // Ignoruj wynik, jeśli użytkownik zdążył już zmienić PID.
      if (lookupSequence.current[playerNumber] !== requestId) return;

      const player = response?.data;
      if (
        !player ||
        player.name == null ||
        String(player.name).trim() === "" ||
        player.wk == null ||
        String(player.wk).trim() === ""
      ) {
        setError("Nie znaleziono zawodnika lub odpowiedź z serwera jest niepełna.");
        return;
      }

      setPid(String(player.pid ?? normalizedPid).padStart(5, "0"));
      const cleanName = normalizeCezarName(
        String(player.name)
      );
      setName(cleanName);

      if (!cleanName) {
        setError(
          "Nie udało się odczytać imienia i nazwiska zawodnika."
        );
        return;
      }

      setWK(String(player.wk).replace(",", "."));
    } catch (error) {
      if (lookupSequence.current[playerNumber] !== requestId) return;

      if (error?.response?.status === 404) {
        setError("Nie znaleziono zawodnika o podanym PID.");
      } else {
        setError(
          "Nie udało się pobrać danych z Cezara. Spróbuj ponownie za chwilę."
        );
      }
    } finally {
      if (lookupSequence.current[playerNumber] === requestId) {
        setLoading(false);
      }
    }
  };

  const submitForm = (e) => {
    e.preventDefault();

    const player1Valid =
      PID1 !== "" &&
      Name1.trim() !== "" &&
      WK1 !== "" &&
      !player1Loading &&
      !player1Error &&
      (manual1 ? PID1 === "0" : PID1 !== "0");

    const player2Valid =
      PID2 !== "" &&
      Name2.trim() !== "" &&
      WK2 !== "" &&
      !player2Loading &&
      !player2Error &&
      (manual2 ? PID2 === "0" : PID2 !== "0");

    if (!player1Valid || !player2Valid) return;

    const newCouple = {
      player1: {
        name: Name1.trim(),
        WK: WK1,
        PID: manual1 ? "0" : PID1,
      },
      player2: {
        name: Name2.trim(),
        WK: WK2,
        PID: manual2 ? "0" : PID2,
      },
      tournament: slug,
    };

    addPlayerSubmit(newCouple, slug);
    navigate(`/zapisy/${slug}`);
  };

  const renderNameField = (playerNumber) => {
    const isFirstPlayer = playerNumber === 1;
    const name = isFirstPlayer ? Name1 : Name2;
    const setName = isFirstPlayer ? setName1 : setName2;
    const manual = isFirstPlayer ? manual1 : manual2;
    const label = isFirstPlayer
      ? "Imię i nazwisko pierwszego zawodnika"
      : "Imię i nazwisko drugiego zawodnika";
    const id = isFirstPlayer ? "name1" : "name2";

    return (
      <div className="mb-4" key={id}>
        <label
          htmlFor={id}
          className="block text-gray-700 font-bold mb-2"
        >
          {label}
        </label>
        <input
          type="text"
          id={id}
          name={id}
          className={`border rounded w-full py-2 px-3 ${
            manual ? "bg-white" : "bg-gray-100"
          }`}
          required
          value={name}
          readOnly={!manual}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
        {manual && (
          <p className="mt-1 text-sm text-blue-700">
            PID 0 — wpisz imię i nazwisko ręcznie.
          </p>
        )}
      </div>
    );
  };

  const renderWKField = (playerNumber) => {
    const isFirstPlayer = playerNumber === 1;
    const wk = isFirstPlayer ? WK1 : WK2;
    const setWK = isFirstPlayer ? setWK1 : setWK2;
    const manual = isFirstPlayer ? manual1 : manual2;
    const id = isFirstPlayer ? "WK1" : "WK2";
    const label = isFirstPlayer
      ? "WK pierwszego zawodnika"
      : "WK drugiego zawodnika";

    return (
      <div className="mb-4" key={id}>
        <label htmlFor={id} className="block text-gray-700 font-bold mb-2">
          {label}
        </label>
        {manual ? (
          <select
            id={id}
            name={id}
            className="border rounded w-full py-2 px-3 mb-2"
            required
            value={wk}
            onChange={(e) => setWK(e.target.value)}
          >
            <option value="">Wybierz WK</option>
            {WK_OPTIONS.map((option) => (
              <option value={option} key={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            id={id}
            name={id}
            className="border rounded w-full py-2 px-3 bg-gray-100"
            value={wk}
            readOnly
            required
          />
        )}
      </div>
    );
  };

  const formReady =
    PID1 !== "" &&
    PID2 !== "" &&
    Name1.trim() !== "" &&
    Name2.trim() !== "" &&
    WK1 !== "" &&
    WK2 !== "" &&
    !player1Loading &&
    !player2Loading &&
    !player1Error &&
    !player2Error &&
    (manual1 ? PID1 === "0" : PID1 !== "0") &&
    (manual2 ? PID2 === "0" : PID2 !== "0");

  return (
    <section className="bg-indigo-50 min-h-screen">
      <div className="container mx-auto w-full max-w-2xl px-3 py-8 sm:py-16">
        <div className="mb-4 rounded-md border bg-white p-4 shadow-md sm:p-6">
          <form onSubmit={submitForm}>
            <h2 className="mb-6 text-center text-2xl font-semibold sm:text-3xl">
              Zapisz się do turnieju
            </h2>

            <div className="mb-4">
              <label
                htmlFor="PID1"
                className="mb-2 block font-bold text-gray-700"
              >
                PID pierwszego zawodnika
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="PID1"
                name="PID1"
                className="mb-2 w-full rounded border px-3 py-2"
                required
                maxLength={5}
                pattern="[0-9]{1,5}"
                title="Wpisz PID składający się z 4–5 cyfr; wpisz 0 dla zawodnika niezreszoneo."
                value={PID1}
                onChange={(e) => updatePid(e.target.value, 1)}
                onBlur={() => fetchPlayer(PID1, 1)}
                aria-describedby="pid1-help pid1-status"
              />
              <p id="pid1-help" className="text-sm text-gray-600">
                Wpisz PID zawodnika. Dla osoby niezreszonej wpisz 0.
              </p>
              <div id="pid1-status" aria-live="polite">
                {player1Loading && (
                  <p className="mt-1 text-sm text-gray-600">
                    Pobieranie danych zawodnika...
                  </p>
                )}
                {player1Error && (
                  <p className="mt-1 text-sm text-red-600">{player1Error}</p>
                )}
              </div>
            </div>

            {renderNameField(1)}
            {renderWKField(1)}

            <hr className="my-6" />

            <div className="mb-4">
              <label
                htmlFor="PID2"
                className="mb-2 block font-bold text-gray-700"
              >
                PID drugiego zawodnika
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="PID2"
                name="PID2"
                className="mb-2 w-full rounded border px-3 py-2"
                required
                maxLength={5}
                pattern="[0-9]{1,5}"
                title="Wpisz PID składający się z 1–5 cyfr; wpisz 0 dla zawodnika spoza bazy."
                value={PID2}
                onChange={(e) => updatePid(e.target.value, 2)}
                onBlur={() => fetchPlayer(PID2, 2)}
                aria-describedby="pid2-help pid2-status"
              />
              <p id="pid2-help" className="text-sm text-gray-600">
                Wpisz PID zawodnika. Dla osoby niezreszonej wpisz 0.
              </p>
              <div id="pid2-status" aria-live="polite">
                {player2Loading && (
                  <p className="mt-1 text-sm text-gray-600">
                    Pobieranie danych zawodnika...
                  </p>
                )}
                {player2Error && (
                  <p className="mt-1 text-sm text-red-600">{player2Error}</p>
                )}
              </div>
            </div>

            {renderNameField(2)}
            {renderWKField(2)}

            <button
              className="mt-2 w-full rounded-full bg-indigo-500 px-4 py-3 font-bold text-white hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
              type="submit"
              disabled={!formReady}
            >
              Dodaj parę
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RegistrationForm;
